import { useMemo } from "react";

import { endOfMonth, format, startOfMonth } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import { DateRange } from "@/jenesys-types/jenesys-entities/config";

import {
  selectDateRange,
  setDateRange
} from "@/store/features/companies/companies";

const today = new Date();

const defaultDateRange: DateRange = {
  label: "Current Month",
  before: today,
  after: startOfMonth(today)
};

export const useRangeSelector = (
  _clientId?: string,
  initialDateRange: DateRange = defaultDateRange
) => {
  const dispatch = useDispatch();
  const dateRange = useSelector(selectDateRange);
  /**
   * The function `monthsList` creates an array of objects representing the current month and
   * previous months of the current year with timestamp information.
   * @returns An array of objects representing the current month and previous months of the current year.
   * Each object contains a label with the month and year (e.g., "July 2024"), a timestamp for the last
   * date of the month (before), and a timestamp for the first date of the month (after).
   */
  const monthsList = useMemo(() => {
    const currentMonth = today.getMonth(); // Get current month index (0-based)
    const currentYear = today.getFullYear();

    const months = [];

    // Current Month
    months.push({
      label: "Current Month",
      before: today, // timestamp of the current date
      after: startOfMonth(today) // timestamp of the first date of the current month
    });

    // Previous Months of the current year
    for (let i = currentMonth - 1; i >= 0; i -= 1) {
      const monthDate = new Date(currentYear, i);
      months.push({
        label: format(monthDate, "MMMM yyyy"), // e.g., "July 2024"
        before: endOfMonth(monthDate), // timestamp of the last date of the month
        after: startOfMonth(monthDate) // timestamp of the first date of the month
      });
    }

    // Add the previous year's last 3 months
    for (let i = 11; i > 8; i -= 1) {
      const monthDate = new Date(currentYear - 1, i);
      months.push({
        label: format(monthDate, "MMMM yyyy"), // e.g., "July 2024"
        before: endOfMonth(monthDate), // timestamp of the last date of the month
        after: startOfMonth(monthDate) // timestamp of the first date of the month
      });
    }

    return months;
  }, []);

  const handleRangeReset = () => {
    dispatch(setDateRange(initialDateRange));
  };

  const handleSetDateRange = (newDateRange: DateRange) => {
    dispatch(setDateRange(newDateRange));
  };

  return {
    dateRange: dateRange ?? initialDateRange,
    monthsList,
    setDateRange: handleSetDateRange,
    handleRangeReset
  };
};
